import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { format } from 'date-fns';
import { NgxChartsModule, Color, ScaleType, LegendPosition } from '@swimlane/ngx-charts';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from '../../../../services/analytics.service';
import { AnalyticsFilters, ChartData } from '../../../../models/analytics.models';
import { APPLICATIONS_COLOR_SCHEME } from '../../analytics-colors';

/**
 * Applications Chart Component
 * Displays applications analytics with multi-line time series
 * Shows total applications, unique candidates, and unique jobs over time
 * SSR-safe: Only renders charts in browser context
 */
@Component({
  selector: 'app-applications-chart',
  standalone: true,
  imports: [CommonModule, NgxChartsModule, MatProgressSpinnerModule, MatCardModule, TranslocoModule],
  templateUrl: './applications-chart.component.html',
  styleUrls: ['./applications-chart.component.scss'],
})
export class ApplicationsChartComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  @Input() filters!: AnalyticsFilters;
  @Input() legendPosition: LegendPosition = LegendPosition.Right;

  // Raw API data storage for re-transformation on language change
  private rawData: any = null;

  // Chart data - multi-series for applications
  timeSeriesData: ChartData[] = [];

  // Summary stats
  totalApplications = 0;
  totalUniqueJobs = 0;
  avgPerJob = 0;

  // Time series chart configuration
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  animations = true;

  // Color scheme - multi-line chart with 3 series
  // Red for applications, amber for candidates, blue for jobs
  timeSeriesColorScheme: Color = APPLICATIONS_COLOR_SCHEME;

  loading = false;
  error: string | null = null;

  constructor(private analyticsService: AnalyticsService, private translocoService: TranslocoService) {}

  ngOnInit(): void {
    // Only load chart data on browser
    if (this.isBrowser && this.filters) {
      this.loadChartData();
    }

    // Subscribe to language changes and re-transform data
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.rawData) {
          this.transformAllData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when filters change
    if (changes['filters'] && !changes['filters'].firstChange && this.isBrowser) {
      this.loadChartData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load chart data from API
   */
  private loadChartData(): void {
    if (!this.filters) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.analyticsService
      .getApplications(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          // Store raw data for language change re-transformation
          this.rawData = data;
          // Transform all chart data with current language
          this.transformAllData();
          this.loading = false;
        },
        error: (err) => {
          console.error('Chart data error:', err);
          this.error = this.translocoService.translate('analytics.errorLoadChartData');
          this.loading = false;
        },
      });
  }

  /**
   * Transform all chart data with current language
   * Called on initial load and when language changes
   */
  private transformAllData(): void {
    if (!this.rawData) {
      return;
    }

    // Transform time series data
    this.timeSeriesData = this.transformTimeSeriesData(this.rawData.timeSeries);

    // Set summary statistics
    this.totalApplications = this.rawData.totalApplications;
    this.totalUniqueJobs = this.rawData.totalUniqueJobs;
    this.avgPerJob = this.rawData.avgApplicationsPerJob || 0;
  }

  /**
   * Transform time series data to ngx-charts multi-line format
   * Format: [
   *   { name: 'Total Applications', series: [{ name: 'Jan 01', value: 145 }] },
   *   { name: 'Unique Candidates', series: [{ name: 'Jan 01', value: 89 }] },
   *   { name: 'Unique Jobs', series: [{ name: 'Jan 01', value: 42 }] }
   * ]
   */
  private transformTimeSeriesData(timeSeries: Array<{ date: string; count: number; uniqueCandidates: number; uniqueJobs: number }>): ChartData[] {
    if (!timeSeries || timeSeries.length === 0) {
      return [];
    }

    return [
      {
        name: this.translocoService.translate('analytics.totalApplications'),
        series: timeSeries.map((item) => ({
          name: format(new Date(item.date), 'MMM dd'),
          value: item.count,
        })),
      },
      {
        name: this.translocoService.translate('analytics.uniqueCandidates'),
        series: timeSeries.map((item) => ({
          name: format(new Date(item.date), 'MMM dd'),
          value: item.uniqueCandidates,
        })),
      },
      {
        name: this.translocoService.translate('analytics.uniqueJobs'),
        series: timeSeries.map((item) => ({
          name: format(new Date(item.date), 'MMM dd'),
          value: item.uniqueJobs,
        })),
      },
    ];
  }

  /**
   * Format Y-axis to show only whole numbers
   */
  yAxisTickFormatting(value: number): string {
    return Math.floor(value) === value ? value.toString() : '';
  }

  /**
   * Handle chart select event (optional)
   */
  onSelect(event: any): void {
    console.log('Chart item selected:', event);
  }
}
